import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Spin, Row, Col, Tabs, Card, Button, Input, List, Modal, Divider, message, Collapse, Tag } from "antd";
import { SearchOutlined, TeamOutlined, UserOutlined, FileAddOutlined, FireOutlined, ExportOutlined } from "@ant-design/icons";
import { fetchClaimDetails, fetchClaimdetailsList, getClaimDetailsEmployeeData, get_claimdetails_list_export } from "../../Action/Actions";
// import { useSelectedYear } from "../../Context/SelectYearContext"; // --- REMOVED
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice"; // +++ ADDED
import { getUserData } from "../login/LoginSlice";
import DataTable from "../DataTable/DataTable";
import * as XLSX from "xlsx";

const ClaimDetails = () => {
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();
  const location = useLocation();
  const AuthData = useSelector(getUserData);

  // +++ ADDED: Get financial year data from Redux store
  const selectedYearId = useSelector(selectSelectedYearId);
  const financialYears = useSelector(selectFinancialYears);
  
  // Find the selected year's display name from the year list
  const selectedYear = financialYears.find(year => year.id === selectedYearId)?.financial_year || "Not Selected";

  const [activeTab, setActiveTab] = useState("");
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);
  // const { selectedYear } = useSelectedYear(); // --- REMOVED
  const [claimData, setClaimData] = useState({});
  const [loading, setLoading] = useState(false);
  const policyTypes = Object.keys(claimData || {});
  const { Panel } = Collapse;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [dependentData, setDependentData] = useState({});
  const user = useSelector(getUserData);
  const [selectedPolicyid, setSelectedPolicyid] = useState(null);
  const [claimDatatable, setClaimDatatable] = useState([]);
  const excludeKeys = [
    "id",
    "TPA_ID",
    "UNIQUE_CLAIM",
    "ref_policy_id",
    // add any other keys you want to exclude
  ];

  useEffect(() => {
    const fetchClaimData = async () => {
      if (!AuthData?.corporate_id || !selectedYearId) {
        setLoading(false);
        setClaimData({});
        setSelectedPolicyid(null);
        setSelectedPolicyNumber(null);
        setClaimDatatable([]);
        return;
      }
      try {
        setLoading(true);
        // Fetch claim details (policies list)
        const data = await dispatch(fetchClaimdetailsList(AuthData?.corporate_id));
        const { policy_claim_details } = data;

        // Set claim data
        setClaimData(policy_claim_details || {});
        
        // Get policy types that actually have data (not empty arrays)
        const availableTabs = Object.keys(policy_claim_details || {}).filter(
          (tab) => policy_claim_details[tab] && policy_claim_details[tab].length > 0
        );
        
        // Use the first available type that has data
        const tabToUse = availableTabs.length > 0 ? availableTabs[0] : "";
        
        // Update activeTab to the first available type with data
        if (tabToUse && tabToUse !== activeTab) {
          setActiveTab(tabToUse);
        }
      } catch (err) {
        console.error("Error fetching claim details data:", err);
        setClaimData({});
        setClaimDatatable([]);
        setSelectedPolicyid(null);
        setSelectedPolicyNumber(null);
      } finally {
        setLoading(false);
      }
    };

    fetchClaimData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, AuthData?.corporate_id, selectedYearId]);

  // Second useEffect: Watch for claimData and activeTab changes, fetch table data
  useEffect(() => {
    const fetchTableData = async () => {
      if (!claimData || Object.keys(claimData).length === 0) {
        return;
      }

      const tabToUse = activeTab;
      
      if (
        claimData[tabToUse] &&
        claimData[tabToUse].length > 0
      ) {
        const firstPolicy = claimData[tabToUse][0];
        // Use the same logic as filter: policy_number if available, otherwise policy_id
        const policyNumber = firstPolicy.policy_details?.policy_number?.trim() || firstPolicy.policy_details?.policy_id;
        const policyId = firstPolicy.policy_details?.policy_id;
        
        setSelectedPolicyNumber(policyNumber);
        setSelectedPolicyid(policyId);
        
        // Fetch table data for this policy
        if (policyId) {
          try {
            setLoading(true);
            const tableDataResponse = await dispatch(fetchClaimDetails(policyId, 1));
            if (tableDataResponse && tableDataResponse.claimlistdata) {
              const { claimlistdata } = tableDataResponse;
              setClaimDatatable(claimlistdata?.data || []);
              setPagination((prev) => ({
                ...prev,
                current: claimlistdata?.current_page || 1,
                total: claimlistdata?.total || 0,
              }));
            } else {
              setClaimDatatable([]);
            }
          } catch (err) {
            console.error("Error fetching table data:", err);
            setClaimDatatable([]);
          } finally {
            setLoading(false);
          }
        }
      } else {
        setSelectedPolicyid(null);
        setSelectedPolicyNumber(null);
        setClaimDatatable([]);
        setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
      }
    };

    fetchTableData();
  }, [dispatch, claimData, activeTab, setPagination]);

  const fetchClaimTable = async (policyid, page = 1) => {
    try {
      if (AuthData?.corporate_id && policyid) {
        setLoading(true);
        // This thunk will get the selectedYearId from the redux store automatically
        const data = await dispatch(fetchClaimDetails(policyid, page));
        setClaimDatatable(data.claimlistdata?.data || []);
        setPagination((prev) => ({
          ...prev,
          current: data.claimlistdata?.current_page,
          total: data.claimlistdata?.total,
        }));
      }
    } catch (err) {
      console.error("Error fetching claim details data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    const firstPolicy = claimData[key]?.[0]?.policy_details;
    if (firstPolicy) {
      // Use policy_number if available, fallback to policy_id as key for "Policy not yet issued"
      const displayValue = firstPolicy.policy_number?.trim() || firstPolicy.policy_id;
      setSelectedPolicyNumber(displayValue);
      setSelectedPolicyid(firstPolicy.policy_id);
      // Fetch table data for the first policy in this tab
      if (firstPolicy.policy_id) {
        fetchClaimTable(firstPolicy.policy_id, 1);
      }
    } else {
      setSelectedPolicyNumber(null);
      setSelectedPolicyid(null);
    }
  };

  const handlePolicyNumberClick = (policyNumber) => {
    setSelectedPolicyNumber(policyNumber);
    
    // Find the policy data that matches this policy number
    const matchingPolicy = claimData[activeTab]?.find(policy => {
      const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
      return key === policyNumber;
    });
    
    if (matchingPolicy) {
      const policyId = matchingPolicy.policy_details?.policy_id;
      setSelectedPolicyid(policyId);
      
      // Reset pagination and fetch data for this policy
      setPagination(prev => ({ ...prev, current: 1 }));
      
      if (policyId) {
        fetchClaimTable(policyId, 1);
      }
    }
  };

  const handleTableChange = (pagination) => {
    fetchClaimTable(selectedPolicyid, pagination.current);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const filteredDependentData = Object.entries(dependentData)
    .filter(([key, value]) => key !== 'id' && key !== 'ref_policy_id' && value && value.toString().toLowerCase().includes(searchQuery.toLowerCase()))
    .map(([key, value]) => ({
      label: key.replace(/_/g, ' ').toUpperCase(),
      value: value || 'N/A',
    }));

  const filteredData = claimData[activeTab] || [];
  const paginatedData = filteredData.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination({
      current: page,
      pageSize,
      total: filteredDependentData.length,
    });
  };

  const handleViewDependent = async (id) => {
    try {
      const data = await dispatch(getClaimDetailsEmployeeData(id));
      console.log("Dependent Data:", data);
      setDependentData(data || []);
      setIsModalVisible(true);
    } catch (error) {
      message.error("Failed to fetch Employee data");
    }
  };

  const getTabIcon = (type) => {
    switch (type) {
      case "GMC":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL":
        return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy":
        return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup":
        return <FileAddOutlined style={{ marginRight: 8 }} />;
      case "Fire":
        return <FireOutlined style={{ marginRight: 8 }} />;
      default:
        return null;
    }
  };

  const handleDownloadCSV = () => {
    dispatch(get_claimdetails_list_export(selectedPolicyid))
      .then((response) => {
        console.log("CSV download response:", response);
        window.open(response.data.download_link, "_blank");
      })
      .catch((error) => {
        console.error("Error downloading CSV:", error);
        message.error("Failed to download CSV file.");
      });
  };

  const PolicyDetailsTable = ({ policy }) => {
    const columns = [
      {
        title: "SI NO",
        dataIndex: "si_no",
        key: "si_no",
        render: (_, __, index) => {
          const pageIndex = pagination.current || 1;
          const pageSize = pagination.pageSize || 10;
          return (pageIndex - 1) * pageSize + index + 1;
        },
      },
      {
        title: "DETAILS",
        dataIndex: "details",
        key: "details",
        render: (_, details) =>

          <Button
            className="d-flex-table"
            onClick={() => handleViewDependent(details.id)}
            type="button"
            style={{ background: "#1890ff", color: "#fff" }}
          >
            View
          </Button>

      },
      {
        title: "EMPLOYEE ID",
        dataIndex: "employee_code",
        key: "employee_code",
      },
      {
        title: "EMPLOYEE NAME",
        dataIndex: "employee_name",
        key: "employee_name",
      },
      {
        title: "BENEFICIARY NAME",
        dataIndex: "beneficiary_name",
        key: "beneficiary_name",
      },
      {
        title: "RELATION",
        dataIndex: "relation",
        key: "relation",
      },
      {
        title: "CLAIM TYPE",
        dataIndex: "claim_type",
        key: "claim_type",
      },
      {
        title: "CLAIM STATUS",
        dataIndex: "claim_status",
        key: "claim_status",
      },
      {
        title: "CLAIM NO",
        dataIndex: "claim_no",
        key: "claim_no",
      },
      {
        title: "TPA CLAIM NO",
        dataIndex: "tpa_claim_no",
        key: "tpa_claim_no",
      },
      {
        title: "HOSPITALLIZATION DATE",
        dataIndex: "hospitalization_date",
        key: "hospitalization_date",
      },
      {
        title: "HOSPITAL NAME",
        dataIndex: "hospital_name",
        key: "hospital_name",
      },
      {
        title: "DISCHARGE DATE",
        dataIndex: "discharge_date",
        key: "discharge_date",
      },
      {
        title: "AMOUNT CLAIMED",
        dataIndex: "amount_claimed",
        key: "amount_claimed",
      },
      {
        title: "AMOUNT SANCTIONED",
        dataIndex: "amount_sanctioned",
        key: "amount_sanctioned",
      },
      {
        title: "GENDER",
        dataIndex: "gender",
        key: "gender",
      },
      {
        title: "HOSPITAL STATE",
        dataIndex: "hospital_state",
        key: "hospital_state",
      },
      {
        title: "NETWORK / NON NETWORK",
        dataIndex: "network_non_network",
        key: "network_non_network",
      },
      {
        title: "MEDICAL SURGICAL",
        dataIndex: "medical/surgical",
        key: "medical/surgical",
      },
      {
        title: "LEVEL OF CARE",
        dataIndex: "level_of_care",
        key: "level_of_care",
      },
      {
        title: "DISEASE",
        dataIndex: "disease",
        key: "disease",
      },
      {
        title: "CITY",
        dataIndex: "city",
        key: "city",
      },
      {
        title: "AGE",
        dataIndex: "age",
        key: "age",
      },
      {
        title: "CLAIM FILED DATE",
        dataIndex: "claim_filed_date",
        key: "claim_filed_date",
      },
      {
        title: "CLAIM SETTLED DATE",
        dataIndex: "claim_settled_date",
        key: "claim_settled_date",
      },
      {
        title: "DISEASE CATEGORY",
        dataIndex: "disease_category",
        key: "disease_category",
      },
      {
        title: "CLAIM REGISTER DATE",
        dataIndex: "claim_register_date",
        key: "claim_register_date",
      },
      {
        title: "INTIMATION METHOD",
        dataIndex: "intimation_method",
        key: "intimation_method",
      },
      {
        title: "SUM INSURED",
        dataIndex: "sum_insured",
        key: "sum_insured",
      },
      {
        title: "TDS AMOUNT",
        dataIndex: "tds_amount",
        key: "tds_amount",
      },
      {
        title: "DEDUCTION AMOUNT",
        dataIndex: "deduction_amount",
        key: "deduction_amount",
      },
      {
        title: "DEDUCTION REASON",
        dataIndex: "deduction_reason",
        key: "deduction_reason",
      },
      {
        title: "DEFICIENCY INTIMATED DATE",
        dataIndex: "deficiency_intimated_date",
        key: "deficiency_intimated_date",
      },
      {
        title: "DEFICIENCY SUBMISSION DATE",
        dataIndex: "deficiency_submission_date",
        key: "deficiency_submission_date",
      },
      {
        title: "ICD CODER",
        dataIndex: "icd_code",
        key: "icd_code",
      },
      {
        title: "CLAIM PAID AMOUNT",
        dataIndex: "claim_paid_amount",
        key: "claim_paid_amount",
      },
      {
        title: "CLOSURE REASON",
        dataIndex: "closure_reason",
        key: "closure_reason",
      },
      {
        title: "DEFICIENCY REASON",
        dataIndex: "deficiency_reason",
        key: "deficiency_reason",
      },
      {
        title: "CLAIM SUB STATUS",
        dataIndex: "claim_sub_status",
        key: "claim_sub_status",
      },
      {
        title: "INSURANCE CLAIM NO",
        dataIndex: "insurance_claim_no",
        key: "insurance_claim_no",
      },
      {
        title: "CLAIM DOCS",
        dataIndex: "claims_docs",
        key: "claims_docs",
      },
    ];

    return (
      <Card style={{ width: "100%" }}>
        <Card>
          <Collapse defaultActiveKey={["1"]} className="custom-collapse">
            <Panel header="Policy Details" key="1">
              <Row gutter={[16, 16]}>
                <Col className="col-lg-4 col-md-4 collapse-content">
                  <strong>Policy Number:</strong> {policy.policy_details.policy_number || "N/A"}
                </Col>
                <Col className="col-lg-4 col-md-4 collapse-content">
                  <strong>Policy Start Date:</strong> {policy.policy_details?.policy_startdate || "N/A"}
                </Col>
                <Col className="col-lg-4 col-md-4 collapse-content">
                  <strong>Policy End Date:</strong> {policy.policy_details?.policy_end_date || "N/A"}
                </Col>
                <Col className="col-lg-4 col-md-4 collapse-content">
                  <strong>Insurer:</strong> {policy.policy_details?.policy_select_insurer || "N/A"}
                </Col>
                <Col className="col-lg-4 col-md-4 collapse-content">
                  <strong>TPA:</strong> {policy.policy_details?.policy_select_tpa || "N/A"}
                </Col>
              </Row>
            </Panel>
          </Collapse>
          <div className="row justify-content-center px-3 py-3 bg-label">
            <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
              <Card className="label-card" style={{ height: "100%" }}>
                <div className="d-flex gap-2">
                  <div className="my-auto label-icon-bg">
                    <UserOutlined className="label-icon" />
                  </div>
                  <div className="label-sec">
                    <h6>Claims paid</h6>
                    <ul style={{ paddingLeft: "0px" }}>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Amount{"  "}-{"  "}  </span>
                        <span style={{ textAlign: "right", flex: 1 }}>{policy.claim_count?.claims_paid || "0"}</span>
                      </li>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Count{"  "}-{"  "}  </span>
                        <span style={{ marginLeft: "10px" }}>{policy.claim_count?.claims_paid_count || "0"}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
              <Card className="label-card" style={{ height: "100%" }}>
                <div className="d-flex gap-2">
                  <div className="my-auto label-icon-bg">
                    <UserOutlined className="label-icon" />
                  </div>
                  <div className="label-sec">
                    <h6>Claims under process</h6>
                    <ul style={{ paddingLeft: "0px" }}>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Amount{"  "}-{"  "}</span>
                        <span>{policy.claim_count?.claims_underprocess || "0"}</span>
                      </li>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Count{"  "}-{"  "}</span>
                        <span style={{ marginLeft: "10px" }}>{policy.claim_count?.claims_underprocess_count || "0"}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
              <Card className="label-card" style={{ height: "100%" }}>
                <div className="d-flex gap-2">
                  <div className="my-auto label-icon-bg">
                    <UserOutlined className="label-icon" />
                  </div>
                  <div className="label-sec">
                    <h6>Claims closed / Rejected</h6>
                    <ul style={{ paddingLeft: "0px" }}>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Amount{"  "}-{"  "}</span>
                        <span>{policy.claim_count?.claims_closed || "0"}</span>
                      </li>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Count{"  "}-{"  "}</span>
                        <span style={{ marginLeft: "10px" }}>{policy.claim_count?.claims_closed_count || "0"}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
              <Card className="label-card" style={{ height: "100%" }}>
                <div className="d-flex gap-2">
                  <div className="my-auto label-icon-bg">
                    <UserOutlined className="label-icon" />
                  </div>
                  <div className="label-sec">
                    <h6>Reported Claims</h6>
                    <ul style={{ paddingLeft: "0px" }}>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Amount{"  "}-{"  "}</span>
                        <span>{policy.claim_count?.reported_claims || "0"}</span>
                      </li>
                      <li style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>Count{"  "}-{"  "}</span>
                        <span style={{ marginLeft: "10px" }}>{policy.claim_count?.reported_claims_count || "0"}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>
          <Row
            gutter={16}
            className="mb-3 justify-content-between align-items-center px-3"
            style={{ display: "flex", justifySelf: "flex-end" }}
          >
            <Col xs={24} sm={2} md={2} lg={2} xl={2}>
              <Button
                type="primary"
                onClick={handleDownloadCSV}
                icon={<ExportOutlined />}
                className="d-flex"
              >
                Export
              </Button>
            </Col>
          </Row>
          <DataTable
            columns={columns}
            dataSource={claimDatatable || []}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `Showing ${range[0]} to ${range[1]} of ${total} entries`,
            }}
            onChange={handleTableChange}
          />
        </Card>
      </Card>
    );
  };

  const tabItems = policyTypes?.map((type) => ({
    key: type,
    label: (
      <>
        <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          {type}
          {getTabIcon(type)}
        </span>
      </>
    ),
    children: (
      <>
        <Tabs
          activeKey={selectedPolicyNumber}
          onChange={handlePolicyNumberClick}
          items={claimData[type]?.map((policy) => {
            // Use the same logic: policy_number if available, otherwise policy_id
            const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
            return {
              key: key,
              label: policy.policy_details?.policy_number?.trim() ? (
                policy.policy_details.policy_number.trim()
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  
                  <Tag
                    style={{
                      backgroundColor: "#FFFF00",
                      color: "#000000E0",
                      fontSize: "12px",
                      padding: "2px 8px",
                      margin: 0,
                      fontFamily: '"Plus Jakarta Sans", sans-serif',
                      borderRadius: "4px",
                    }}
                  >
                    Policy Number not issued
                  </Tag>
                </span>
              ),
            };
          }) || []}
        />
        {selectedPolicyNumber &&
          claimData[type]?.some(
            (policy) => {
              const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
              return key === selectedPolicyNumber;
            }
          ) ? (
          <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
            {claimData[type]
              .filter((policy) => {
                const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
                return key === selectedPolicyNumber;
              })
              .map((policy) => {
                const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
                return (
                  <Col key={key} span={24}>
                    <PolicyDetailsTable policy={policy} />
                  </Col>
                );
              })}
          </Row>
        ) : (<div className="escalation-message">No Claim Details Found.</div>)}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            <span style={{ marginLeft: "8px" }}>
              {/* This now uses the 'selectedYear' variable derived from Redux state */}
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            className="esc-tab"
            items={tabItems}
          />
        ) : (
          <Card style={{ marginTop: "20px" }}>
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <p style={{ fontSize: "16px", fontWeight: "500" }}>No claim details data available for {selectedYear}</p>
            </div>
          </Card>
        )}
      </div>
      <Modal
        title="Employee Claim Details"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={800}
      >
        <Divider />
        <div style={{ maxHeight: 500, overflowY: "auto" }}>
          <List
            size="large"
            bordered
            dataSource={
              dependentData
                ? Object.entries(dependentData)
                  .filter(([key]) => !excludeKeys.includes(key))
                  .map(([key, value]) => ({
                    label: key.replace(/_/g, ' ').toUpperCase(),
                    value: value !== undefined && value !== null && value !== "" ? value : "N/A",
                  }))
                : []
            }
            renderItem={(item) => (
              <List.Item style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 500 }}>{item.label}:</span>
                <span style={{ marginLeft: 16 }}>
                  {item.value}
                </span>
              </List.Item>
            )}
          />
        </div>
      </Modal>

    </Spin>
  );
};

export default ClaimDetails;